import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Calendar, X, Save, Calculator, HelpCircle, BookOpen, Users, Clock, Archive, Trash2, LayoutGrid, List } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { formatDate } from '../utils/dateUtils';
import { STATUTS_PROJET, TYPES_FACTURATION } from '../data/mockData';
import { getCategoryBadgeStyle, AVAILABLE_CATEGORIES } from '../utils/categoryUtils';
import { useRealTimePhaseCalculation } from '../hooks/useRealTimePhaseCalculation';
import PhaseSection from '../components/PhaseSection';
import ProjectSummary from '../components/ProjectSummary';
import {
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  differenceInDays,
  isWithinInterval
} from 'date-fns';

// Composant Planning View
const PlanningView = ({ projets, periodDates }) => {
  const today = new Date();
  const startDate = periodDates.start;
  const endDate = periodDates.end;
  const totalDays = differenceInDays(endDate, startDate) + 1;
  
  // Calculer les positions en CSS pour les barres de gantt
  const getProjectPosition = (projet) => {
    const projectStart = projet.date_debut ? new Date(projet.date_debut) : null;
    const projectEnd = projet.date_fin ? new Date(projet.date_fin) : null;
    
    if (!projectStart || !projectEnd) return null;
    
    // Déterminer si le projet est dans la période affichée
    if (projectEnd < startDate || projectStart > endDate) return null;
    
    // Calculer le décalage et la largeur
    const actualStart = projectStart < startDate ? startDate : projectStart;
    const actualEnd = projectEnd > endDate ? endDate : projectEnd;
    
    const offsetDays = differenceInDays(actualStart, startDate);
    const width = differenceInDays(actualEnd, actualStart) + 1;
    
    return {
      offset: (offsetDays / totalDays) * 100,
      width: (width / totalDays) * 100
    };
  };

  // Trier les projets par date de début
  const sortedProjets = [...(projets || [])].sort((a, b) => {
    const dateA = a.date_debut ? new Date(a.date_debut) : new Date('9999-12-31');
    const dateB = b.date_debut ? new Date(b.date_debut) : new Date('9999-12-31');
    return dateA - dateB;
  });

  // Générer les jours de la semaine pour l'en-tête
  const headerDates = [];
  for (let i = 0; i < totalDays; i += Math.ceil(totalDays / 12)) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    headerDates.push(date);
  }

  const getStatusColor = (statut) => {
    // Couleur de fond du badge (clair)
    switch (statut) {
      case 'en_cours': return 'bg-green-100';
      case 'planifie': return 'bg-blue-100';
      case 'termine': return 'bg-gray-100';
      default: return 'bg-yellow-100';
    }
  };

  const getStatusTextColor = (statut) => {
    // Couleur de texte du badge (foncé)
    switch (statut) {
      case 'en_cours': return 'text-green-800';
      case 'planifie': return 'text-blue-800';
      case 'termine': return 'text-gray-800';
      default: return 'text-yellow-800';
    }
  };

  const getStatusLabel = (statut) => {
    const statusObj = STATUTS_PROJET.find(s => s.value === statut);
    return statusObj ? statusObj.label : statut;
  };

  return (
    <div className="p-6">
      {/* Titre et légende */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline des Projets</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-gray-600">Planifié</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-gray-600">En cours</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-500 rounded"></div>
            <span className="text-gray-600">Terminé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-red-500"></div>
            <span className="text-gray-600">Aujourd'hui</span>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* En-tête avec dates */}
          <div className="flex mb-2">
            <div className="w-56 flex-shrink-0 pr-4">
              <div className="text-sm font-semibold text-gray-900">Projet</div>
            </div>
            <div className="flex-1 flex">
              {headerDates.map((date, idx) => (
                <div
                  key={idx}
                  className="flex-1 text-xs text-gray-500 text-center border-l border-gray-200"
                >
                  {date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })}
                </div>
              ))}
            </div>
          </div>

          {/* Barre de temps de référence */}
          <div className="flex mb-6 pb-6 border-b-2 border-gray-200">
            <div className="w-56 flex-shrink-0 pr-4"></div>
            <div className="flex-1 relative h-8">
              {/* Ligne de grille */}
              <div className="absolute inset-0 flex">
                {headerDates.map((date, idx) => (
                  <div
                    key={idx}
                    className="flex-1 border-l border-gray-100"
                  ></div>
                ))}
              </div>
              {/* Ligne d'aujourd'hui */}
              {isWithinInterval(today, { start: startDate, end: endDate }) && (
                <div
                  className="absolute top-0 bottom-0 w-1 bg-red-500"
                  style={{
                    left: `${(differenceInDays(today, startDate) / totalDays) * 100}%`
                  }}
                ></div>
              )}
            </div>
          </div>

          {/* Projets */}
          <div className="space-y-1">
            {sortedProjets.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucun projet à afficher dans cette période</p>
              </div>
            ) : (
              sortedProjets.map((projet) => {
                const position = getProjectPosition(projet);
                if (!position) return null;

                // Extraire les initiales des personnes affectées de la liste API
                const getPersonneInitials = () => {
                  // Utiliser la liste personnes_affectees fournie par l'API
                  if (!projet?.personnes_affectees || !Array.isArray(projet.personnes_affectees)) {
                    return [];
                  }
                  
                  // Récupérer les initiales
                  const personnesSet = new Set();
                  projet.personnes_affectees.forEach(personne => {
                    if (personne.nom) {
                      const initials = personne.nom
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase())
                        .join('')
                        .substring(0, 2);
                      if (initials) personnesSet.add(initials);
                    }
                  });
                  
                  return Array.from(personnesSet).slice(0, 5); // Max 5 initiales
                };

                const personneInitials = getPersonneInitials();

                return (
                  <div key={projet.id}>

                    {/* Conteneur flex pour barre + infos */}
                    <div className="flex items-center bg-slate-50 rounded-md p-2">
                      {/* Colonne gauche vide (pour alignement) */}
                      <div className="w-56 flex-shrink-0 pr-4 text-left">
                        {/* Nom du projet */}
                    
                          <Link
                            to={`/projets/${projet.id}`}
                            className="block"
                          >
                            <div className="text-sm font-medium text-gray-900 truncate hover:text-primary-600">
                              {projet.nom}
                            </div>
                          </Link>

                          {/* Catégorie du projet et initiales */}
                          <div className="mt-1 flex items-center gap-2">
                            {/* Tag catégorie */}
                            {projet.categorie && (
                              <span 
                                className="inline-flex px-2 py-1 text-xs font-medium rounded-full text-white"
                                style={getCategoryBadgeStyle(projet.categorie)}
                                title={`Catégorie: ${projet.categorie}`}
                              >
                                {projet.categorie}
                              </span>
                            )}

                            {/* Initiales des personnes */}
                            {personneInitials.length > 0 && (
                              <div className="flex gap-0.5">
                                {personneInitials.map((initial, idx) => (
                                  <span 
                                    key={idx}
                                    className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold"
                                    title={`Personne avec initiales ${initial}`}
                                  >
                                    {initial}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                       
                      </div>
                      
                      {/* Colonne droite avec barre et infos */}
                      <div className="flex-1">
                        {/* Barre gantt */}
                        <div className="relative h-10 mb-1">
                          {/* Grille de fond */}
                          <div className="absolute inset-0 flex pointer-events-none">
                            {headerDates.map((date, idx) => (
                              <div
                                key={idx}
                                className="flex-1 border-l border-gray-100"
                              ></div>
                            ))}
                          </div>

                          {/* Barre du projet */}
                          <div
                            className={`absolute top-2 h-8 rounded-md shadow-sm transition-all hover:shadow-md cursor-pointer flex items-center px-2 ${getStatusColor(projet.statut)}`}
                            style={{
                              left: `${position.offset}%`,
                              width: `${position.width}%`,
                              minWidth: '60px'
                            }}
                            title={`${projet.nom} - ${formatDate(projet.date_debut)} à ${formatDate(projet.date_fin)}`}
                          >
                            <span className={`text-xs font-medium truncate ${getStatusTextColor(projet.statut)}`}>
                              {getStatusLabel(projet.statut)}
                            </span>
                          </div>
                        </div>

                        {/* Infos sous la barre - en ligne, alignées avec la barre gantt */}
                        <div 
                          className="flex items-center gap-3 text-xs py-1"
                          style={{ marginLeft: `${position.offset}%` }}
                        >
                          
                          {/* Jours prévus */}
                          <span className="text-gray-600 font-medium">
                            {projet.budget_total || 0}j prévus
                          </span>

                          {/* Jours consommés */}
                          <span className="text-gray-600 font-medium">
                            {projet.consomme_total || 0}j cons.
                          </span>

                          {/* Facturation */}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            projet.facturable 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {projet.facturable ? 'Fact.' : 'Non fact.'}
                          </span>

                          {/* Initiales des personnes */}
                          {personneInitials.length > 0 && (
                            <div className="flex gap-1">
                              {personneInitials.map((initial, idx) => (
                                <span 
                                  key={idx}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold"
                                  title={`Personne avec initiales ${initial}`}
                                >
                                  {initial}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Légende des informations */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Légende de dates</h4>
          <p className="text-sm text-gray-600">
            <strong>Début :</strong> {formatDate(periodDates.start)}<br />
            <strong>Fin :</strong> {formatDate(periodDates.end)}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Total des projets</h4>
          <p className="text-sm text-gray-600">
            <strong>{sortedProjets.length}</strong> projet(s) affiché(s)
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Astuce</h4>
          <p className="text-sm text-gray-600">
            Cliquez sur un projet pour voir les détails
          </p>
        </div>
      </div>
    </div>
  );
};

const Projets = () => {
  const { state, getFilteredProjets, actions } = useApp();
  const projets = getFilteredProjets();
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'planning'
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    date_debut: '',
    date_fin: '',
    budget_analyse_design: '',
    budget_build: '',
    budget_test: '',
    budget_jours: 0,
    statut: 'planifie',
    facturable: true,
    categorie: 'Autre',
    date_debut_analyse_design: '',
    date_fin_analyse_design: '',
    date_debut_build: '',
    date_fin_build: '',
    date_debut_test: '',
    date_fin_test: '',
    // Nouvelles données pour les phases avec affectations
    phases: {
      analyse_design: {
        budget_jours: 0,
        date_debut_calculee: '',
        date_fin_calculee: '',
        affectations: []
      },
      build: {
        budget_jours: 0,
        date_debut_calculee: '',
        date_fin_calculee: '',
        affectations: []
      },
      test: {
        budget_jours: 0,
        date_debut_calculee: '',
        date_fin_calculee: '',
        affectations: []
      }
    },
    // Dates calculées automatiquement
    date_debut_calculee: '',
    date_fin_calculee: ''
  });
  const [errors, setErrors] = useState({});
  
  // État pour la modale d'aide
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Hook pour le calcul en temps réel des dates de phases
  const calculatedDates = useRealTimePhaseCalculation(
    formData, 
    state.personnes?.data || []
  );

  // Récupérer les dates de période de manière stable
  const periodDates = React.useMemo(() => {
    const periodSelection = state.periodSelection;
    const now = new Date();
    
    if (periodSelection.type === 'preset') {
      switch (periodSelection.preset) {
        case 'current_month':
          return { start: startOfMonth(now), end: endOfMonth(now) };
        case 'current_quarter':
          return { start: startOfQuarter(now), end: endOfQuarter(now) };
        case 'fiscal_year': {
          // Exercice en cours : du 1er juillet dernier au 30 juin prochain
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth(); // 0-11
          
          // Si nous sommes avant le 1er juillet (mois 6), l'exercice a commencé l'année dernière
          if (currentMonth < 6) {
            const start = new Date(currentYear - 1, 6, 1); // 1er juillet année dernière
            const end = new Date(currentYear, 5, 30); // 30 juin année courante
            return { start, end };
          } else {
            // Si nous sommes le 1er juillet ou après, l'exercice a commencé cette année
            const start = new Date(currentYear, 6, 1); // 1er juillet année courante
            const end = new Date(currentYear + 1, 5, 30); // 30 juin année prochaine
            return { start, end };
          }
        }
        case '6_months':
          return { start: startOfMonth(now), end: endOfMonth(new Date(now.getFullYear(), now.getMonth() + 5, now.getDate())) };
        default:
          return { start: startOfMonth(now), end: endOfMonth(now) };
      }
    } else if (periodSelection.type === 'custom') {
      return { 
        start: periodSelection.startDate ? new Date(periodSelection.startDate) : startOfMonth(now), 
        end: periodSelection.endDate ? new Date(periodSelection.endDate) : endOfMonth(now) 
      };
    } else if (periodSelection.type === 'single') {
      const date = periodSelection.singleDate ? new Date(periodSelection.singleDate) : now;
      return { start: date, end: date };
    }
    
    return { start: startOfMonth(now), end: endOfMonth(now) };
  }, [state.periodSelection]);

  // Recharger les projets et personnes quand la période change
  React.useEffect(() => {
    if (state.auth.isAuthenticated && periodDates) {
      // Recharger les projets pour récupérer les données actualisées
      actions.loadProjets();
      // Les personnes peuvent aussi être rechargées pour des calculs liés
      actions.loadPersonnes();
    }
  }, [state.auth.isAuthenticated, periodDates, actions]);

  // Calculer automatiquement le budget total
  useEffect(() => {
    const analyseDesign = parseFloat(formData.budget_analyse_design) || 0;
    const build = parseFloat(formData.budget_build) || 0;
    const test = parseFloat(formData.budget_test) || 0;
    const total = analyseDesign + build + test;
    
    setFormData(prev => ({
      ...prev,
      budget_jours: total
    }));
  }, [formData.budget_analyse_design, formData.budget_build, formData.budget_test]);

  // Synchroniser les dates calculées avec le formData
  useEffect(() => {
    if (calculatedDates.projet) {
      setFormData(prev => ({
        ...prev,
        date_debut_calculee: calculatedDates.projet.date_debut,
        date_fin_calculee: calculatedDates.projet.date_fin,
        // Mettre à jour les dates traditionnelles pour compatibilité
        date_debut: calculatedDates.projet.date_debut ? calculatedDates.projet.date_debut.toISOString().split('T')[0] : '',
        date_fin: calculatedDates.projet.date_fin ? calculatedDates.projet.date_fin.toISOString().split('T')[0] : ''
      }));
    }
  }, [calculatedDates]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear submit error when user modifies any field
    if (errors.submit) {
      setErrors(prev => ({ ...prev, submit: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validation des informations générales (étape 1)
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom du projet est requis';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    // Validation des temps et dates (étape 2)
    if (!formData.date_debut_analyse_design) {
      newErrors.date_debut_analyse_design = 'La date de début du projet est requise';
    }
    
    // Validation des budgets par phase
    const analyseDesign = parseFloat(formData.budget_analyse_design) || 0;
    const build = parseFloat(formData.budget_build) || 0;
    const test = parseFloat(formData.budget_test) || 0;
    
    if (analyseDesign < 0) {
      newErrors.budget_analyse_design = 'Les temps planifiés ne peuvent pas être négatifs';
    }
    
    if (build < 0) {
      newErrors.budget_build = 'Les temps planifiés ne peuvent pas être négatifs';
    }
    
    if (test < 0) {
      newErrors.budget_test = 'Les temps planifiés ne peuvent pas être négatifs';
    }
    
    if (formData.budget_jours <= 0) {
      newErrors.budget_total = 'Le total des temps planifiés doit être supérieur à 0';
    }
    
    // Validation des phases et affectations
    let hasValidPhases = false;
    const phasesOrder = ['analyse_design', 'build', 'test'];
    const phaseNames = {
      analyse_design: 'Analyse & Design',
      build: 'Développement',
      test: 'Tests'
    };
    
    phasesOrder.forEach(phaseKey => {
      const budgetField = `budget_${phaseKey}`;
      const budget = parseFloat(formData[budgetField]) || 0;
      const phase = formData.phases?.[phaseKey];
      
      if (budget > 0) {
        hasValidPhases = true;
        
        // Vérifier qu'il y a au moins une affectation valide pour cette phase
        const validAffectations = phase?.affectations?.filter(aff => 
          aff.id_personne && 
          aff.pourcentage_temps > 0 && 
          aff.pourcentage_temps <= 100
        ) || [];
        
        if (validAffectations.length === 0) {
          newErrors[`phase_${phaseKey}_affectations`] = 
            `La phase ${phaseNames[phaseKey]} nécessite au moins une personne affectée`;
        }
        
        // Validation des affectations individuelles
        phase?.affectations?.forEach((affectation, index) => {
          if (affectation.id_personne) {
            if (!affectation.pourcentage_temps || affectation.pourcentage_temps < 1 || affectation.pourcentage_temps > 100) {
              newErrors[`${phaseKey}_affectation_${index}_pourcentage`] = 
                'Le pourcentage doit être entre 1 et 100';
            }
            
            if (!affectation.role_projet) {
              newErrors[`${phaseKey}_affectation_${index}_role`] = 
                'Veuillez sélectionner un rôle';
            }
          }
        });
      }
    });
    
    if (!hasValidPhases) {
      newErrors.phases_general = 'Au moins une phase doit avoir un budget et des affectations';
    }
    
    // Validation basée sur les dates calculées
    if (calculatedDates.projet) {
      if (calculatedDates.projet.has_errors) {
        newErrors.calculated_dates = 'Certaines phases ont des erreurs de configuration';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation finale avant soumission
    if (!validateForm()) {
      return;
    }

    try {
      // Utiliser les dates calculées automatiquement
      const date_debut = calculatedDates.projet?.date_debut 
        ? calculatedDates.projet.date_debut.toISOString().split('T')[0]
        : formData.date_debut_analyse_design;
      
      const date_fin = calculatedDates.projet?.date_fin 
        ? calculatedDates.projet.date_fin.toISOString().split('T')[0]
        : formData.date_debut_analyse_design;
      
      // Préparer les données des phases avec dates calculées
      const phasesData = {};
      ['analyse_design', 'build', 'test'].forEach(phaseKey => {
        const phaseCalc = calculatedDates[phaseKey];
        if (phaseCalc) {
          phasesData[phaseKey] = {
            budget_jours: parseFloat(formData[`budget_${phaseKey}`]) || 0,
            date_debut_calculee: phaseCalc.date_debut ? phaseCalc.date_debut.toISOString().split('T')[0] : null,
            date_fin_calculee: phaseCalc.date_fin ? phaseCalc.date_fin.toISOString().split('T')[0] : null,
            duree_semaines: phaseCalc.duree_semaines || 0,
            capacite_equipe: phaseCalc.capacite_equipe || 0,
            affectations: formData.phases[phaseKey]?.affectations?.filter(aff => 
              aff.id_personne && aff.pourcentage_temps > 0
            ) || []
          };
        }
      });
      
      const projetData = {
        nom: formData.nom,
        description: formData.description,
        date_debut: date_debut,
        date_fin: date_fin,
        budget_jours: formData.budget_jours,
        statut: formData.statut,
        facturable: formData.facturable,
        categorie: formData.categorie,
        // Budgets par phase (pour compatibilité)
        budget_analyse_design: parseFloat(formData.budget_analyse_design) || 0,
        budget_build: parseFloat(formData.budget_build) || 0,
        budget_test: parseFloat(formData.budget_test) || 0,
        // Dates calculées par phase
        date_debut_analyse_design: phasesData.analyse_design?.date_debut_calculee || formData.date_debut_analyse_design,
        date_fin_analyse_design: phasesData.analyse_design?.date_fin_calculee || null,
        date_debut_build: phasesData.build?.date_debut_calculee || null,
        date_fin_build: phasesData.build?.date_fin_calculee || null,
        date_debut_test: phasesData.test?.date_debut_calculee || null,
        date_fin_test: phasesData.test?.date_fin_calculee || null,
        // Nouvelles données des phases avec affectations
        phases_data: phasesData,
        // Métadonnées du calcul
        duree_totale_semaines: calculatedDates.projet?.duree_totale_semaines || 0,
        capacite_moyenne: calculatedDates.projet?.capacite_moyenne || 0,
        phases_actives: calculatedDates.projet?.phases_actives || 0
      };
      
      // Log pour débogage (à retirer en production)
      console.log('Données du projet à créer:', projetData);
      
      await actions.createProjet(projetData);
      
      // Succès : Reset form and close modal
      setFormData({
        nom: '',
        description: '',
        date_debut: '',
        date_fin: '',
        budget_analyse_design: '',
        budget_build: '',
        budget_test: '',
        budget_jours: 0,
        statut: 'planifie',
        facturable: true,
        categorie: 'Autre',
        date_debut_analyse_design: '',
        date_fin_analyse_design: '',
        date_debut_build: '',
        date_fin_build: '',
        date_debut_test: '',
        date_fin_test: '',
        phases: {
          analyse_design: {
            budget_jours: 0,
            date_debut_calculee: '',
            date_fin_calculee: '',
            affectations: []
          },
          build: {
            budget_jours: 0,
            date_debut_calculee: '',
            date_fin_calculee: '',
            affectations: []
          },
          test: {
            budget_jours: 0,
            date_debut_calculee: '',
            date_fin_calculee: '',
            affectations: []
          }
        },
        date_debut_calculee: '',
        date_fin_calculee: ''
      });
      setErrors({});
      setCurrentStep(1);
      setShowModal(false);
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      
      // En cas d'erreur, afficher le message d'erreur et garder la modale ouverte
      setErrors(prev => ({
        ...prev,
        submit: 'Une erreur est survenue lors de la création du projet. Veuillez réessayer.'
      }));
      
      // La modale reste ouverte et les données sont conservées
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentStep(1);
    setFormData({
      nom: '',
      description: '',
      date_debut: '',
      date_fin: '',
      budget_analyse_design: '',
      budget_build: '',
      budget_test: '',
      budget_jours: 0,
      statut: 'planifie',
      facturable: true,
      categorie: 'Autre',
      date_debut_analyse_design: '',
      date_fin_analyse_design: '',
      date_debut_build: '',
      date_fin_build: '',
      date_debut_test: '',
      date_fin_test: '',
      phases: {
        analyse_design: {
          budget_jours: 0,
          date_debut_calculee: '',
          date_fin_calculee: '',
          affectations: []
        },
        build: {
          budget_jours: 0,
          date_debut_calculee: '',
          date_fin_calculee: '',
          affectations: []
        },
        test: {
          budget_jours: 0,
          date_debut_calculee: '',
          date_fin_calculee: '',
          affectations: []
        }
      },
      date_debut_calculee: '',
      date_fin_calculee: ''
    });
    setErrors({});
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    
    if (currentStep === 1) {
      // Validation de l'étape 1 : informations générales
      if (!formData.nom.trim()) {
        newErrors.nom = 'Le nom du projet est requis';
      }
      if (!formData.description.trim()) {
        newErrors.description = 'La description est requise';
      }
    } else if (currentStep === 2) {
      // Validation de l'étape 2 : validation souple pour permettre la navigation
      
      // Validation de la date de début (obligatoire)
      if (!formData.date_debut_analyse_design) {
        newErrors.date_debut_analyse_design = 'La date de début du projet est requise';
      }
      
      // Validation des budgets (seulement si remplis)
      const analyseDesign = parseFloat(formData.budget_analyse_design) || 0;
      const build = parseFloat(formData.budget_build) || 0;
      const test = parseFloat(formData.budget_test) || 0;
      
      if (formData.budget_analyse_design && analyseDesign < 0) {
        newErrors.budget_analyse_design = 'Les temps planifiés ne peuvent pas être négatifs';
      }
      if (formData.budget_build && build < 0) {
        newErrors.budget_build = 'Les temps planifiés ne peuvent pas être négatifs';
      }
      if (formData.budget_test && test < 0) {
        newErrors.budget_test = 'Les temps planifiés ne peuvent pas être négatifs';
      }
      
      // Validation des affectations (seulement si la phase a un budget)
      const phasesOrder = ['analyse_design', 'build', 'test'];
      const phaseNames = {
        analyse_design: 'Analyse & Design',
        build: 'Développement',
        test: 'Tests'
      };
      
      phasesOrder.forEach(phaseKey => {
        const budgetField = `budget_${phaseKey}`;
        const budget = parseFloat(formData[budgetField]) || 0;
        const phase = formData.phases?.[phaseKey];
        
        // Si la phase a un budget, vérifier les affectations
        if (budget > 0) {
          const validAffectations = phase?.affectations?.filter(aff => 
            aff.id_personne && 
            aff.pourcentage_temps > 0 && 
            aff.pourcentage_temps <= 100
          ) || [];
          
          if (validAffectations.length === 0) {
            newErrors[`phase_${phaseKey}_warning`] = 
              `La phase ${phaseNames[phaseKey]} devrait avoir au moins une personne affectée`;
          }
          
          // Validation des pourcentages (erreurs critiques)
          phase?.affectations?.forEach((affectation, index) => {
            if (affectation.id_personne && affectation.pourcentage_temps) {
              if (affectation.pourcentage_temps < 1 || affectation.pourcentage_temps > 100) {
                newErrors[`${phaseKey}_affectation_${index}_pourcentage`] = 
                  'Le pourcentage doit être entre 1 et 100';
              }
            }
          });
        }
      });
    }
    // Pas de validation pour l'étape 3 - elle sera validée lors de la soumission finale
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      nextStep();
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projets</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérez vos projets et suivez leur progression
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded transition-colors flex items-center ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Vue liste"
            >
              <List className="h-4 w-4 mr-1" />
              <span className="text-sm font-medium">Liste</span>
            </button>
            <button
              onClick={() => setViewMode('planning')}
              className={`px-3 py-2 rounded transition-colors flex items-center ${
                viewMode === 'planning'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Vue planning"
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              <span className="text-sm font-medium">Planning</span>
            </button>
          </div>
          <button
            onClick={() => setShowHelpModal(true)}
            className="btn-secondary"
            title="Aide et documentation"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Aide
          </button>
          <button 
            onClick={() => setShowModal(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau projet
          </button>
        </div>
      </div>

      <div className="mt-8">
        {/* VUE LISTE */}
        {viewMode === 'list' && (
          <div className="card overflow-hidden">
            {/* En-tête du tableau */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-700 uppercase tracking-wide">
                <div className="col-span-3">Projet</div>
                <div className="col-span-2">Période</div>
                <div className="col-span-2">Budget</div>
                <div className="col-span-2">Consommé</div>
                <div className="col-span-2">Progression par phase</div>
                <div className="col-span-1">Actions</div>
              </div>
            </div>
            
            {/* Liste des projets */}
            <div className="divide-y divide-gray-200">
              {projets?.map((projet) => (
                <div key={projet.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Informations du projet */}
                    <div className="col-span-3">
                      <div className="flex items-start space-x-3">
                        <div className="flex-1 min-w-0 text-left">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {projet.nom}
                          </h3>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                            {projet.description}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {projet.categorie && (
                              <span 
                                className="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                                style={getCategoryBadgeStyle(projet.categorie)}
                              >
                                {projet.categorie}
                              </span>
                            )}
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              projet.statut === 'en_cours' ? 'bg-green-100 text-green-800' :
                              projet.statut === 'planifie' ? 'bg-blue-100 text-blue-800' :
                              projet.statut === 'termine' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {STATUTS_PROJET.find(s => s.value === projet.statut)?.label || projet.statut}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              projet.facturable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {projet.facturable ? 'Facturable' : 'Non facturable'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Période */}
                    <div className="col-span-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-900 font-medium">
                            {formatDate(projet.date_debut)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(projet.date_fin)}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Budget planifié */}
                    <div className="col-span-2">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">
                          {projet.budget_total || 0} jours
                        </p>
                        <p className="text-xs text-gray-500">Budget planifié</p>
                      </div>
                    </div>
                    
                    {/* Budget consommé et restant */}
                    <div className="col-span-2">
                      <div className="text-sm space-y-1">
                        <p className="font-medium text-gray-900">
                          {projet.consomme_total || 0} jours
                        </p>
                        <p className={`text-xs font-medium ${
                          (projet.budget_total || 0) - (projet.consomme_total || 0) >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          Restant: {((projet.budget_total || 0) - (projet.consomme_total || 0)).toFixed(1)}j
                        </p>
                      </div>
                    </div>
                    
                    {/* Progression par phase */}
                    <div className="col-span-2">
                      <div className="space-y-2">
                        {/* Phase Analyse & Design */}
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 bg-blue-500 rounded-sm flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className="h-1.5 rounded-full bg-blue-500 transition-all duration-300"
                                style={{ 
                                  width: `${parseFloat(projet.planifie_analyse_design) > 0 
                                    ? Math.min(((projet.consomme_analyse_design || 0) / parseFloat(projet.planifie_analyse_design)) * 100, 100) 
                                    : 0
                                  }%` 
                                }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-xs text-gray-600 font-medium w-10 text-right">
                            {parseFloat(projet.planifie_analyse_design) > 0 
                              ? Math.round(((projet.consomme_analyse_design || 0) / parseFloat(projet.planifie_analyse_design)) * 100)
                              : 0
                            }%
                          </span>
                        </div>

                        {/* Phase Build */}
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 bg-green-500 rounded-sm flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className="h-1.5 rounded-full bg-green-500 transition-all duration-300"
                                style={{ 
                                  width: `${parseFloat(projet.planifie_build) > 0 
                                    ? Math.min(((projet.consomme_build || 0) / parseFloat(projet.planifie_build)) * 100, 100) 
                                    : 0
                                  }%` 
                                }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-xs text-gray-600 font-medium w-10 text-right">
                            {parseFloat(projet.planifie_build) > 0 
                              ? Math.round(((projet.consomme_build || 0) / parseFloat(projet.planifie_build)) * 100)
                              : 0
                            }%
                          </span>
                        </div>

                        {/* Phase Test */}
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 bg-purple-500 rounded-sm flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className="h-1.5 rounded-full bg-purple-500 transition-all duration-300"
                                style={{ 
                                  width: `${parseFloat(projet.planifie_test) > 0 
                                    ? Math.min(((projet.consomme_test || 0) / parseFloat(projet.planifie_test)) * 100, 100) 
                                    : 0
                                  }%` 
                                }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-xs text-gray-600 font-medium w-10 text-right">
                            {parseFloat(projet.planifie_test) > 0 
                              ? Math.round(((projet.consomme_test || 0) / parseFloat(projet.planifie_test)) * 100)
                              : 0
                            }%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="col-span-1">
                      <Link
                        to={`/projets/${projet.id}`}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        Voir →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* VUE PLANNING */}
        {viewMode === 'planning' && (
          <div className="card overflow-hidden">
            <PlanningView projets={projets} periodDates={periodDates} />
          </div>
        )}
      </div>

      {projets?.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Aucun projet trouvé</p>
        </div>
      )}

      {/* Modal pour créer un nouveau projet */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-5xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouveau projet</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Stepper */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-4">
                  {/* Étape 1 */}
                  <div className={`flex items-center ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
                      currentStep >= 1 ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-400 border-gray-300'
                    }`}>
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium">Informations générales</span>
                  </div>

                  {/* Ligne de connexion */}
                  <div className={`w-16 h-0.5 ${currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-300'}`}></div>

                  {/* Étape 2 */}
                  <div className={`flex items-center ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
                      currentStep >= 2 ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-400 border-gray-300'
                    }`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium">Temps et dates</span>
                  </div>

                  {/* Ligne de connexion */}
                  <div className={`w-16 h-0.5 ${currentStep >= 3 ? 'bg-primary-600' : 'bg-gray-300'}`}></div>

                  {/* Étape 3 */}
                  <div className={`flex items-center ${currentStep >= 3 ? 'text-primary-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
                      currentStep >= 3 ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-400 border-gray-300'
                    }`}>
                      3
                    </div>
                    <span className="ml-2 text-sm font-medium">Statut et facturation</span>
                  </div>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              {/* Étape 1: Informations générales */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h3>
                  
                  {/* Nom du projet */}
                  <div>
                    <label htmlFor="nom" className="form-label">
                      Nom du projet *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      className={`form-input ${errors.nom ? 'border-red-500' : ''}`}
                      placeholder="Ex: Refonte Site E-commerce"
                    />
                    {errors.nom && (
                      <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="form-label">
                      Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      className={`form-input ${errors.description ? 'border-red-500' : ''}`}
                      placeholder="Décrivez les objectifs et le scope du projet..."
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                    )}
                  </div>

                  {/* Catégorie */}
                  <div>
                    <label htmlFor="categorie" className="form-label">
                      Catégorie
                    </label>
                    <select
                      id="categorie"
                      name="categorie"
                      value={formData.categorie}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      {AVAILABLE_CATEGORIES.map((categorie) => (
                        <option key={categorie.value} value={categorie.value}>
                          {categorie.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-sm text-gray-500">
                      La catégorie détermine la couleur d'affichage du projet
                    </p>
                  </div>
                </div>
              )}

              {/* Étape 2: Temps, dates et affectations */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Temps, dates et affectations</h3>
                  
                  {/* Note sur le calcul automatique */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Calcul automatique :</strong> Les dates de fin sont calculées en temps réel 
                      en fonction des affectations et de la capacité des personnes. Affectez des personnes 
                      à chaque phase pour voir les dates se calculer automatiquement.
                    </p>
                  </div>

                  {/* Date de début de projet */}
                  <div>
                    <label htmlFor="date_debut_analyse_design" className="form-label">
                      Date de début du projet *
                    </label>
                    <input
                      type="date"
                      id="date_debut_analyse_design"
                      name="date_debut_analyse_design"
                      value={formData.date_debut_analyse_design}
                      onChange={handleInputChange}
                      className={`form-input ${errors.date_debut_analyse_design ? 'border-red-500' : ''}`}
                    />
                    {errors.date_debut_analyse_design && (
                      <p className="mt-1 text-sm text-red-600">{errors.date_debut_analyse_design}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Cette date sera utilisée comme début de la première phase (Analyse & Design)
                    </p>
                  </div>

                  {/* Phases avec affectations */}
                  <div className="space-y-6">
                    <PhaseSection
                      phaseKey="analyse_design"
                      phaseName="Analyse & Design"
                      phaseColor="blue"
                      formData={formData}
                      setFormData={setFormData}
                      calculatedDates={calculatedDates}
                      personnesData={state.personnes?.data || []}
                      errors={errors}
                    />
                    
                    <PhaseSection
                      phaseKey="build"
                      phaseName="Développement"
                      phaseColor="green"
                      formData={formData}
                      setFormData={setFormData}
                      calculatedDates={calculatedDates}
                      personnesData={state.personnes?.data || []}
                      errors={errors}
                    />
                    
                    <PhaseSection
                      phaseKey="test"
                      phaseName="Tests"
                      phaseColor="purple"
                      formData={formData}
                      setFormData={setFormData}
                      calculatedDates={calculatedDates}
                      personnesData={state.personnes?.data || []}
                      errors={errors}
                    />
                  </div>

                  {/* Total des temps planifiés */}
                  <div className="p-3 bg-primary-50 rounded-lg border border-primary-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calculator className="h-4 w-4 text-primary-600 mr-2" />
                        <span className="text-sm font-medium text-primary-900">
                          Total des temps planifiés
                        </span>
                      </div>
                      <span className="text-lg font-bold text-primary-900">
                        {formData.budget_jours} jours
                      </span>
                    </div>
                    {errors.budget_total && (
                      <p className="mt-1 text-sm text-red-600">{errors.budget_total}</p>
                    )}
                  </div>

                  {/* Résumé du projet */}
                  <ProjectSummary calculatedDates={calculatedDates} formData={formData} />


                </div>
              )}

              {/* Étape 3: Statut et facturation */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Statut et facturation</h3>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Statut */}
                    <div>
                      <label htmlFor="statut" className="form-label">
                        Statut
                      </label>
                      <select
                        id="statut"
                        name="statut"
                        value={formData.statut}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        {STATUTS_PROJET.map((statut) => (
                          <option key={statut.value} value={statut.value}>
                            {statut.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Facturable */}
                    <div>
                      <label htmlFor="facturable" className="form-label">
                        Type de facturation
                      </label>
                      <select
                        id="facturable"
                        name="facturable"
                        value={formData.facturable}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        {TYPES_FACTURATION.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-sm text-gray-500">
                        {formData.facturable 
                          ? 'Le temps passé sera facturable au client'
                          : 'Projet interne, temps non facturable'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Messages d'erreur généraux */}
              {errors.submit && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    {errors.submit}
                  </p>
                </div>
              )}
              
              {errors.phases_general && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    {errors.phases_general}
                  </p>
                </div>
              )}
              
              {errors.calculated_dates && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    {errors.calculated_dates}
                  </p>
                </div>
              )}

              {/* Boutons de navigation */}
              <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
                <div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="btn-secondary"
                    >
                      Précédent
                    </button>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn-primary"
                    >
                      Suivant
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={state.loading}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {state.loading ? 'Création...' : 'Créer le projet'}
                    </button>
                  )}
                  
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale d'aide */}
      {showHelpModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mr-4">
                  <BookOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Guide des Projets</h2>
                  <p className="text-sm text-gray-600 mt-1">Tout ce que vous devez savoir sur la gestion des projets</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Section 1: Création d'un projet */}
              <section>
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full mr-3">
                    <Plus className="h-4 w-4 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Créer un nouveau projet</h3>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-green-900 mb-2">Processus de création en 3 étapes :</h4>
                  <div className="space-y-3 text-sm text-green-800">
                    <div className="flex items-start">
                      <div className="flex items-center justify-center w-6 h-6 bg-green-200 text-green-800 rounded-full text-xs font-bold mr-3 mt-0.5">1</div>
                      <div>
                        <strong>Informations générales :</strong> Nom, description et catégorie du projet
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="flex items-center justify-center w-6 h-6 bg-green-200 text-green-800 rounded-full text-xs font-bold mr-3 mt-0.5">2</div>
                      <div>
                        <strong>Temps et affectations :</strong> Budget par phase et attribution des personnes
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="flex items-center justify-center w-6 h-6 bg-green-200 text-green-800 rounded-full text-xs font-bold mr-3 mt-0.5">3</div>
                      <div>
                        <strong>Statut et facturation :</strong> Configuration finale du projet
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Calculator className="h-4 w-4 mr-2 text-blue-600" />
                      Budgets par phase
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Analyse & Design :</strong> Conception et spécifications</li>
                      <li>• <strong>Développement :</strong> Implémentation du code</li>
                      <li>• <strong>Tests :</strong> Validation et recette</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-purple-600" />
                      Affectation des personnes
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Sélectionnez les personnes pour chaque phase</li>
                      <li>• Définissez le pourcentage de temps (1-100%)</li>
                      <li>• Assignez un rôle projet à chaque personne</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 2: Calcul automatique des dates */}
              <section>
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <Clock className="h-4 w-4 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Calcul automatique des dates</h3>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">🤖 Intelligence artificielle de planification</h4>
                  <p className="text-sm text-blue-800">
                    Les dates de fin sont calculées automatiquement en fonction de la capacité des personnes affectées et de leur disponibilité.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Comment ça marche ?</h4>
                    <div className="space-y-3 text-sm text-gray-600">
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <div>
                          <strong>1. Capacité individuelle :</strong> Chaque personne a un indice de productivité et une capacité hebdomadaire
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <div>
                          <strong>2. Calcul d'équipe :</strong> La capacité totale de l'équipe est calculée pour chaque phase
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <div>
                          <strong>3. Durée optimisée :</strong> Le système détermine la durée nécessaire en fonction du budget et de la capacité
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <div>
                          <strong>4. Phases séquentielles :</strong> Les phases s'enchaînent automatiquement dans l'ordre logique
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-900 mb-2">💡 Conseil</h4>
                    <p className="text-sm text-yellow-800">
                      Plus vous affectez de personnes compétentes à une phase, plus elle se terminera rapidement. 
                      L'indice de productivité de chaque personne est pris en compte dans le calcul.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 3: Modification des projets */}
              <section>
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-orange-100 rounded-full mr-3">
                    <Calendar className="h-4 w-4 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Modifier un projet</h3>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-orange-900 mb-2">Accès à la modification</h4>
                  <p className="text-sm text-orange-800">
                    Cliquez sur "Voir →" dans la liste des projets, puis sur "Modifier le projet" dans le détail.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Éléments modifiables</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Nom et description</li>
                      <li>• Dates de début et fin</li>
                      <li>• Budgets par phase</li>
                      <li>• Planification des phases</li>
                      <li>• Statut du projet</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Modification rapide</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Statut :</strong> Clic direct dans le détail</li>
                      <li>• <strong>Temps :</strong> Bouton "Ajouter du temps"</li>
                      <li>• <strong>Suivi :</strong> Graphiques temps réel</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 4: Suppression et archivage */}
              <section>
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full mr-3">
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Supprimer ou archiver un projet</h3>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-red-900 mb-2">⚠️ Logique intelligente</h4>
                  <p className="text-sm text-red-800">
                    Le système détermine automatiquement s'il faut supprimer ou archiver selon la présence de temps consommé.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                      Suppression complète
                    </h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><strong>Quand :</strong> Aucun temps consommé saisi</p>
                      <p><strong>Action :</strong> Suppression définitive</p>
                      <p><strong>Données supprimées :</strong></p>
                      <ul className="ml-4 space-y-1">
                        <li>• Le projet lui-même</li>
                        <li>• Tous les temps planifiés</li>
                        <li>• Tous les rattachements</li>
                        <li>• Toutes les phases et affectations</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Archive className="h-4 w-4 mr-2 text-yellow-600" />
                      Archivage
                    </h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><strong>Quand :</strong> Temps consommé présent</p>
                      <p><strong>Action :</strong> Archivage sécurisé</p>
                      <p><strong>Changements :</strong></p>
                      <ul className="ml-4 space-y-1">
                        <li>• Statut → "Terminé"</li>
                        <li>• Marqué comme archivé</li>
                        <li>• Date d'archivage enregistrée</li>
                        <li>• Masqué des listes principales</li>
                        <li>• <strong>Toutes les données conservées</strong></li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">🔍 Comment accéder à la suppression/archivage ?</h4>
                  <ol className="text-sm text-gray-600 space-y-1">
                    <li>1. Cliquez sur "Voir →" pour accéder au détail du projet</li>
                    <li>2. Cliquez sur le bouton "Supprimer" ou "Archiver" (selon le cas)</li>
                    <li>3. Confirmez l'action dans la modale qui s'ouvre</li>
                    <li>4. Le système vous redirige automatiquement vers la liste des projets</li>
                  </ol>
                </div>
              </section>

              {/* Section 5: Conseils et bonnes pratiques */}
              <section>
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full mr-3">
                    <HelpCircle className="h-4 w-4 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Conseils et bonnes pratiques</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">✅ À faire</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Définir des budgets réalistes par phase</li>
                      <li>• Affecter les bonnes personnes aux bonnes phases</li>
                      <li>• Utiliser des descriptions claires et détaillées</li>
                      <li>• Mettre à jour le statut régulièrement</li>
                      <li>• Saisir le temps consommé chaque semaine</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">❌ À éviter</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Créer des projets sans affectations</li>
                      <li>• Sous-estimer les budgets de test</li>
                      <li>• Oublier de mettre à jour les statuts</li>
                      <li>• Supprimer un projet avec du temps saisi</li>
                      <li>• Ignorer les alertes de dépassement</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowHelpModal(false)}
                className="btn-primary"
              >
                J'ai compris !
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projets; 